import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ButtonContainer,
  ContentSection,
  Fieldset,
  Heading,
  Layout,
  PageWrap,
  SectionHeading,
} from '@vwfs-bronson/bronson-react';
import { Page } from '../../../..';
import { PaginationComponent } from '../../../../components/Pagination';
import { DealerDashboardSubpage } from '../../../../resources/base/models/enums';
import { routes } from '../../../../routing/routes';
import { getContracts } from '../../../../services/api/miles/miles-services';
import { RunningContractDataWithVehicleData } from '../../../../services/common/miles-pos-running-contract-integration-service/models';
import { assertDefined, isNotesEnabled } from '../../../../services/common/miscUtils';
import { staticSelector, store } from '../../../../services/redux';
import { brokerSelectors } from '../../../../services/redux/features/broker.slice';
import {
  clearSelectedComparisonOfferIds,
  dashboardSelectors,
  resetDashboardSearchForm,
  setDashboardSearchFormWithSelections,
  setRunningContractStatus,
} from '../../../../services/redux/features/dashboard.slice';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import { scrollToTop } from '../../../utils';
import { ButtonAction, Buttons } from '../../constants/ButtonAction';
import { paginationInitialState } from '../../constants/DefaultPagination';
import { DashboardActionPopup } from '../../popups/DashboardPopups';
import { DashBoardSearchSection } from '../components/DashBoardSearchSection';
import { DashboardSubpagesTabs } from '../components/DashboardSubpagesTabs';
import { DashBoardTable } from '../components/DashBoardTable';
import { useDashboardNotesUtils } from '../utils/dashboardNotesUtils';
import { handleSelectionAfterTableUpdate } from '../utils/dashboardSubpageTableSelection';
import { mapResponsePagination } from '../utils/mapResponsePagination';
import { checkIfButtonShouldBeDisabled } from '../utils/multiselectionButtonCheck';
import { RunningContractStatusType, SearchedValues } from '../utils/searchedValues.types';
import { MappedResponseContract } from './models';

const tableHeaders = [
  'offerId',
  ...(isNotesEnabled() ? ['notes'] : []),
  'tradingName',
  'siren',
  'usage',
  'manufacturer',
  'modelDescription',
  'durationMileage',
  'runningContractStatus',
];

const buttonList = [
  { value: 'Copier', label: 'Copier', enableOnMultiSelect: false },
  { value: 'Documents', label: 'Documents', enableOnMultiSelect: false },
] as Buttons;

export const DashboardSubpageRun: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getNotesDashboardButton } = useDashboardNotesUtils();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showCopyPopup, setShowCopyPopup] = useState(false);
  const [searchedValues, setSearchedValues] = useState<SearchedValues>({});
  const [contracts, setContracts] = useState<MappedResponseContract[]>([]);
  const [contractsTotalCount, setContractsTotalCount] = useState<number>(0);
  const [pagination, setPagination] = useState(paginationInitialState);

  const searchForm = useSelector(dashboardSelectors.selectDashboardSearchFormWithSelections);
  const hasStoredSearch = !!searchForm && Object.keys(searchForm).length > 0;

  const runningContractStatus: RunningContractStatusType | undefined = useSelector(
    dashboardSelectors.selectRunningContractStatus
  );

  const mapContracts = (contracts: RunningContractDataWithVehicleData[]): Promise<MappedResponseContract[]> => {
    if (!contracts?.length) {
      return Promise.resolve([]);
    }

    return Promise.all(
      contracts.map((contract) => {
        const {
          reference,
          contractMileage = 0,
          duration,
          vehicleData: { model: { manufacturer = '', description = '' } = {} } = {},
          contractId,
          milesVehiclePaidDate,
        } = contract;
        const button = getNotesDashboardButton('CONTRACT', reference, contractId!);
        const tradingName = contract.applicants?.[0]?.tradingName;
        const customerType = contract.applicants?.[0]?.customerType;

        const cleanTradingName = tradingName?.replace(/[^\w\s]/g, '') ?? '';
        const [lastName = '', firstName = ''] = cleanTradingName.split(', ');
        const isPrivate = customerType === 'INDIVIDUAL';
        return {
          offerId: reference,
          tradingName: `${firstName} ${lastName}`.trim(),
          usage: isPrivate
            ? t('dashboard:table:body:privateCustomerUsage')
            : t('dashboard:table:body:businessCustomerUsage'),
          manufacturer,
          modelDescription: description,
          durationMileage: `${duration}/${contractMileage}`,
          runningContractStatus: milesVehiclePaidDate
            ? t('dashboard:table:body:paid')
            : t('dashboard:table:body:active'),
          contractId,
          notes: button,
          statusId: contract.milesStatusId,
          status: contract.milesStatus,
        };
      })
    );
  };

  const selectedContract = useMemo(() => {
    return contracts.find(({ offerId }) => offerId === selectedItems[0]) || ({} as MappedResponseContract);
  }, [selectedItems, contracts]);

  const tableRef = useRef<{ clearSelections: () => void } | null>(null);

  const resetTableSelections = () => {
    if (tableRef.current) {
      tableRef.current.clearSelections(); // Call the reset method on the child
    }
  };

  const searchFieldsMap = (values: Partial<SearchedValues>): SearchedValues => ({
    ...(values?.searchQuery && { searchQuery: values?.searchQuery }),
    ...(values?.runningContractStatus && { runningContractStatus: values?.runningContractStatus }),
    pageSize: pagination?.pageSize,
  });

  const handleButtonClick = (action: ButtonAction) => {
    switch (action) {
      case 'Copier':
        setShowCopyPopup(true);
        break;
      case 'Documents':
        dispatch(
          setDashboardSearchFormWithSelections({
            ...searchedValues,
            selectedCheckboxes: selectedItems,
            currentPage: pagination?.currentPage,
          })
        );
        navigate.url(
          `${routes.dashboardRunDocuments.replace(':id', selectedContract.contractId ?? '')}?contractId=${selectedContract.contractId}&offerId=${selectedContract.offerId}&contractStatus=${selectedContract.status}`
        );
        break;
      default:
        break;
    }
  };

  const handleConfirm = (action: ButtonAction) => {
    switch (action) {
      case 'Copier':
        setShowCopyPopup(false);
        break;
      default:
        break;
    }
  };

  const onTableSelection = (selectedIds: string[]) => {
    setSelectedItems(selectedIds);
  };

  const doGetQuotes = async (data: Partial<Page> = {}) => {
    const currentCompany = staticSelector(brokerSelectors.currentCompany);
    const milesCompanyIds = [assertDefined(currentCompany?.milesCompanyId)];

    store.dispatch(loadingActions.setLoading(true));
    try {
      const response = await getContracts({
        milesCompanyIds,
        ...(searchForm.currentPage ? { page: searchForm.currentPage } : {}),
        ...data,
      });
      setContractsTotalCount(response?.totalCount);
      const contracts = await mapContracts(response?.quotes);
      setContracts(contracts);
      setSelectedItems((prev) => handleSelectionAfterTableUpdate(prev, contracts, resetTableSelections));
      setPagination(mapResponsePagination(response));
    } finally {
      store.dispatch(loadingActions.setLoading(false));
    }
  };

  useEffect(() => {
    dispatch(clearSelectedComparisonOfferIds());
    const fetchData = async () => {
      if (hasStoredSearch) {
        dispatch(resetDashboardSearchForm());
        doGetQuotes(searchForm);
      } else {
        doGetQuotes({
          ...(runningContractStatus && { runningContractStatus }),
        });

        scrollToTop();
      }
    };

    fetchData();
    return () => {
      dispatch(setRunningContractStatus(undefined));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paginationOnChange = (page: Page) => {
    doGetQuotes({ ...page, ...searchedValues });
    resetTableSelections();
    setSelectedItems([]);
  };

  const searchQuotes = (values: Partial<SearchedValues>) => {
    const newSearchedValues: SearchedValues = searchFieldsMap(values);
    setSearchedValues(newSearchedValues);
    doGetQuotes(newSearchedValues);
  };

  return (
    <>
      <ContentSection pageWrap className="u-pt-none">
        <DashboardSubpagesTabs />
      </ContentSection>

      <ContentSection pageWrap className="u-pv-small">
        <Layout>
          <Layout.Item default="1/1" s="1/1" className="u-mt-none">
            <Heading level={2}>{t('dashboard:search:header')}</Heading>
          </Layout.Item>
        </Layout>
      </ContentSection>

      <PageWrap>
        <Layout>
          <Layout.Item default="1/1" s="1/1" className="u-mr-xxlarge">
            <DashBoardSearchSection
              searchQuotes={searchQuotes}
              subpage={DealerDashboardSubpage.Run}
              searchQuery
              fields={['runningContractStatus']}
            />
          </Layout.Item>
        </Layout>
      </PageWrap>

      <ContentSection pageWrap>
        <Layout>
          <Layout.Item className="u-text-center u-mt-none">
            <SectionHeading level={3} className="u-text-left">
              {t('dashboard:search:results', {
                numResults: contractsTotalCount,
              })}
            </SectionHeading>
            <DashBoardTable ref={tableRef} headers={tableHeaders} onSelection={onTableSelection} values={contracts} />
            <Layout center>
              <PaginationComponent pagination={pagination} onChange={paginationOnChange} />
            </Layout>
          </Layout.Item>
        </Layout>
      </ContentSection>

      <Fieldset.Row>
        <ButtonContainer center>
          <Layout>
            <Layout.Item className="u-text-center">
              {buttonList?.map(({ label, value, enableOnMultiSelect }) => (
                <Button
                  key={`${label}`}
                  value={`${value}`}
                  className={`u-m-small ${value === 'Copier' ? 'u-hide' : ''}`}
                  type="button"
                  secondary
                  onClick={() => handleButtonClick(label)}
                  disabled={checkIfButtonShouldBeDisabled(selectedItems, enableOnMultiSelect)}
                >
                  {label}
                </Button>
              ))}

              {showCopyPopup && (
                <DashboardActionPopup
                  handleClose={() => setShowCopyPopup(false)}
                  onConfirm={() => handleConfirm('Copier')}
                  text={t('dashboard:popup:copyTextTitle')}
                />
              )}
            </Layout.Item>
          </Layout>
        </ButtonContainer>
      </Fieldset.Row>
    </>
  );
};
